<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { pendingUploads, forgetUpload, flushNoteFiles } from '@/lib/noteUploads.js'
import { formatBytes } from '@/lib/format.js'
import { previewKind } from '@/lib/filePreview.js'
import UiModal from '@/components/ui/UiModal.vue'
import UiButton from '@/components/ui/UiButton.vue'
import FilePreview from './FilePreview.vue'
import PdfThumb from './PdfThumb.vue'

/**
 * What a note carries (D-143): photos as thumbnails (opened large), films and recordings played
 * in place — streamed by Core in byte ranges — PDFs with their first page, text files and
 * Office documents as tiles opened in a preview (D-223), other files to download, and files
 * still on this device, waiting for the network.
 */
const props = defineProps({ note: { type: Object, required: true } })
const shown = ref(null)
// The photos of the note, stepped through in the preview: arrows, ← →, a swipe on a phone.
const images = computed(() => files.value.filter((f) => f.kind === 'image'))
const imageIndex = computed(() => (shown.value?.kind === 'image' ? images.value.findIndex((f) => f.uuid === shown.value.uuid) : -1))
const step = (by) => {
  if (imageIndex.value < 0 || images.value.length < 2) return
  shown.value = images.value[(imageIndex.value + by + images.value.length) % images.value.length]
}
const onKey = (e) => {
  if (imageIndex.value < 0) return
  if (e.key === 'ArrowRight') step(1)
  if (e.key === 'ArrowLeft') step(-1)
}
let touchX = null
const onTouchStart = (e) => { touchX = e.changedTouches[0]?.clientX ?? null }
const onTouchEnd = (e) => {
  const x = e.changedTouches[0]?.clientX
  if (touchX != null && x != null && Math.abs(x - touchX) > 40) step(x < touchX ? 1 : -1)
  touchX = null
}
onMounted(() => document.addEventListener('keydown', onKey))
onBeforeUnmount(() => document.removeEventListener('keydown', onKey))
const previewTitle = computed(() => (imageIndex.value >= 0 && images.value.length > 1 ? `${shown.value.name} · ${imageIndex.value + 1} / ${images.value.length}` : shown.value?.name || ''))
const url = (a) => `/api/v1/attachments/${a.uuid}/content`
const files = computed(() => (props.note.attachments || []).map((a) => ({ ...a, kind: previewKind({ mime: a.mime, name: a.name }) || 'file' })))
const tiles = computed(() => files.value.filter((f) => ['pdf', 'text', 'office'].includes(f.kind)))
const tileIcon = (f) => {
  if (f.kind === 'pdf') return 'fa-file-pdf text-red-500'
  if (f.kind === 'text') return 'fa-file-lines text-slate-500'
  const ext = (f.name.match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase()
  if (/^(xls|xlsx|xlsm|xlt|xltx|csv|tsv)$/.test(ext) || /sheet|excel|csv/.test(f.mime || '')) return 'fa-file-excel text-emerald-600'
  if (/^(ppt|pptx|pptm|pps|ppsx|pot|potx)$/.test(ext) || /presentation|powerpoint/.test(f.mime || '')) return 'fa-file-powerpoint text-orange-600'
  return 'fa-file-word text-blue-600'
}
const waiting = computed(() => pendingUploads.byNote[props.note.uuid] || [])
</script>

<template>
  <div v-if="files.length || waiting.length" class="mt-2 space-y-2" data-testid="note-attachments" data-privacy-cells>
    <div v-if="files.some((f) => f.kind === 'image')" class="flex flex-wrap gap-2">
      <button v-for="f in files.filter((x) => x.kind === 'image')" :key="f.uuid" type="button" class="overflow-hidden rounded-lg border border-default" :title="f.name" @click="shown = f">
        <img :src="url(f)" :alt="f.name" loading="lazy" class="h-24 w-24 object-cover" />
      </button>
    </div>
    <video v-for="f in files.filter((x) => x.kind === 'video')" :key="f.uuid" :src="url(f)" controls playsinline preload="metadata" class="max-h-72 w-full rounded-lg bg-black" :aria-label="f.name"></video>
    <audio v-for="f in files.filter((x) => x.kind === 'audio')" :key="f.uuid" :src="url(f)" controls preload="metadata" class="w-full" :aria-label="f.name"></audio>
    <div v-if="tiles.length" class="flex flex-wrap gap-2">
      <button v-for="f in tiles" :key="f.uuid" type="button" class="flex w-28 flex-col overflow-hidden rounded-lg border border-default text-left hover:border-brand-400" :title="f.name" :data-testid="`note-file-${f.kind}`" @click="shown = f">
        <PdfThumb v-if="f.kind === 'pdf'" :src="url(f)" :width="110" class="h-32 w-full items-start" />
        <span v-else class="flex h-32 w-full items-center justify-center bg-[var(--surface-2)]"><i :class="['fa-solid text-3xl', tileIcon(f)]" aria-hidden="true"></i></span>
        <span class="truncate border-t border-default px-1.5 py-1 text-2xs">{{ f.name }}</span>
      </button>
    </div>
    <ul v-if="files.some((f) => f.kind === 'file')" class="space-y-0.5 text-xs">
      <li v-for="f in files.filter((x) => x.kind === 'file')" :key="f.uuid">
        <a :href="`${url(f)}?inline=0`" class="link"><i class="fa-solid fa-paperclip mr-1" aria-hidden="true"></i>{{ f.name }}</a> <span class="text-faint">{{ formatBytes(f.size) }}</span>
      </li>
    </ul>
    <ul v-if="waiting.length" class="space-y-0.5 text-xs" data-testid="note-attachments-waiting">
      <li v-for="w in waiting" :key="w.id" class="flex flex-wrap items-center gap-1.5 text-muted">
        <i :class="['fa-solid', w.error ? 'fa-triangle-exclamation text-red-600' : 'fa-cloud-arrow-up text-blue-500']" aria-hidden="true"></i>
        <span>{{ w.name }} · {{ formatBytes(w.size) }}</span>
        <span v-if="w.error" class="text-red-600">— {{ $t('notes.uploadFailed') }}</span>
        <span v-else>— {{ $t(pendingUploads.sending ? 'notes.sending' : 'notes.waiting') }}</span>
        <UiButton v-if="w.error" size="xs" variant="ghost" icon="fa-rotate-right" @click="flushNoteFiles()">{{ $t('common.retry') }}</UiButton>
        <UiButton v-if="w.error" size="xs" variant="ghost" icon="fa-trash-can" :aria-label="$t('common.remove')" @click="forgetUpload(w.id)" />
      </li>
    </ul>
    <UiModal :open="!!shown" :title="previewTitle" :size="shown?.kind === 'image' ? 'xl' : 'full'" @close="shown = null">
      <div class="relative" data-testid="note-preview" :data-index="imageIndex >= 0 ? imageIndex + 1 : null" @touchstart.passive="onTouchStart" @touchend.passive="onTouchEnd">
        <FilePreview v-if="shown" :key="shown.uuid" :file="shown" :kind="shown.kind" :src="url(shown)" />
        <template v-if="imageIndex >= 0 && images.length > 1">
          <button type="button" class="absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-black/50 px-3 py-2 text-white hover:bg-black/70" :aria-label="$t('notes.previousPhoto')" data-testid="note-preview-prev" @click="step(-1)"><i class="fa-solid fa-chevron-left" aria-hidden="true"></i></button>
          <button type="button" class="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-black/50 px-3 py-2 text-white hover:bg-black/70" :aria-label="$t('notes.nextPhoto')" data-testid="note-preview-next" @click="step(1)"><i class="fa-solid fa-chevron-right" aria-hidden="true"></i></button>
        </template>
      </div>
      <template #footer><a v-if="shown" :href="`${url(shown)}?inline=0`" class="link mr-auto text-sm">{{ $t('common.download') }}</a><UiButton @click="shown = null">{{ $t('common.close') }}</UiButton></template>
    </UiModal>
  </div>
</template>
